import { useCallback } from 'react';

interface VideoPosterResult {
	posterFile: File | null;
	posterUrl: string;
}

export const useGeneratePosterFromVideoAttachment = () => {
	const generateVideoPoster = (videoElement: HTMLVideoElement): Promise<string> => {
		return new Promise((resolve) => {
			const canvas = document.createElement('canvas');
			canvas.width = videoElement.videoWidth;
			canvas.height = videoElement.videoHeight;
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.drawImage(videoElement, 0, 0);
				canvas.toBlob(
					(blob) => {
						if (blob) {
							const posterUrl = URL.createObjectURL(blob);
							resolve(posterUrl);
						} else {
							resolve('');
						}
					},
					'image/jpeg',
					1.0
				);
			} else {
				resolve('');
			}
		});
	};

	return useCallback(async (file: File): Promise<VideoPosterResult> => {
		if (!file.type.startsWith('video/')) {
			return { posterFile: null, posterUrl: '' };
		}

		// Create a video element to generate the poster
		const video = document.createElement('video');
		video.src = URL.createObjectURL(file);
		try {
			// Wait for metadata to load so we can get dimensions
			await new Promise((resolve) => {
				video.onloadedmetadata = resolve;
				video.load();
			});

			const seekTime = 0.01;
			video.currentTime = seekTime;
			// Wait for the seek to complete
			await new Promise((resolve) => {
				video.onseeked = resolve;
			});
			// Generate poster
			const posterDataUrl = await generateVideoPoster(video);
			// Tear down the video element, remove it from the DOM
			// Convert data URL to File
			if (posterDataUrl) {
				const response = await fetch(posterDataUrl);
				const blob = await response.blob();
				const posterFile = new File([blob], 'video-poster.jpg', { type: 'image/jpeg' });
				return { posterFile: posterFile, posterUrl: posterDataUrl };
			}

			return { posterFile: null, posterUrl: '' };
		} finally {
			// Cleanup
			URL.revokeObjectURL(video.src);
		}
	}, []);
};
